import React, { useRef } from "react";
import { Input } from "antd";

export const BULLET = "• ";
const LEADING_BULLET = /^\s*(?:[•·▪◦*-]|\d+[.)])\s*/;

export const toBulletLines = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((line) => line.replace(LEADING_BULLET, "").trim())
    .filter(Boolean);

export const toBulletText = (text) =>
  toBulletLines(text)
    .map((line) => `${BULLET}${line}`)
    .join("\n");

const bulletize = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((line) =>
      line.trim() && !LEADING_BULLET.test(line) ? `${BULLET}${line}` : line
    )
    .join("\n");

const BulletTextArea = ({ value, onChange, onKeyDown, ...props }) => {
  const ref = useRef(null);

  const getTextArea = () => ref.current?.resizableTextArea?.textArea;

  const emit = (newValue, caret) => {
    onChange?.(newValue);
    if (caret === undefined) return;
    requestAnimationFrame(() => {
      const textArea = getTextArea();
      if (textArea) textArea.setSelectionRange(caret, caret);
    });
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    if (!value && newValue.trim()) {
      const bulleted = bulletize(newValue);
      emit(bulleted, bulleted.length);
      return;
    }
    emit(newValue);
  };

  const handleKeyDown = (event) => {
    onKeyDown?.(event);
    if (event.key !== "Enter" || event.shiftKey || props.readOnly) return;

    const text = value || "";
    const { selectionStart, selectionEnd } = event.target;
    const lineStart = text.lastIndexOf("\n", selectionStart - 1) + 1;
    const currentLine = text.slice(lineStart, selectionStart);

    event.preventDefault();
    if (currentLine.trim() === BULLET.trim()) {
      emit(text.slice(0, lineStart) + text.slice(selectionEnd), lineStart);
      return;
    }
    const insert = `\n${BULLET}`;
    emit(
      text.slice(0, selectionStart) + insert + text.slice(selectionEnd),
      selectionStart + insert.length
    );
  };

  return (
    <Input.TextArea
      ref={ref}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
};

export const BulletList = ({ value, text, style }) => {
  const lines = toBulletLines(text ?? value);
  if (!lines.length) return null;
  return (
    <ul
      className="mb-0"
      style={{
        paddingLeft: 18,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        ...style,
      }}
    >
      {lines.map((line, index) => (
        <li key={index}>{line}</li>
      ))}
    </ul>
  );
};

export default BulletTextArea;
